import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Typography, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import globalStyles from '../../../../assets/styles/global.module.scss';
import styles from '../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss';
import carouselStyles from '../../../../assets/styles/componentStyles/templateLibraryStyles/carouselTemplatePreview.module.scss';

import whatsAppDefaultBgImg from "../../../../assets/images/chatWidgetComponents/WhatsAppDefaultBg.png";
import { getWabaName } from '../../../../utils/constants';
import { IntegrationContext, IntegrationContextPayload } from '../../../../context/integrationContext';
import { useTemplateLibraryStore } from '../../../../store/templateLibraryStore';
import { getCarouselCards, getTooltipTextForMessageGuidelines, isTemplateCreated, MAX_CAROUSEL_CARDS } from '../constant';
import { useCarouselFunctions } from '../utils/useCarouselFunctions';

import CarouselTemplateBodyTextPreview from './CarouselTemplateBodyTextPreview';
import CarouselTemplateMediaHeaderPreview from './CarouselTemplateMediaHeaderPreview';
import CarouselTemplateInteractiveActionsPreview from './CarouselTemplateInteractiveActionsPreview';
import CarouselTemplateCardPreview from './CarouselTemplateCardPreview/CarouselTemplateCardPreview';
import CarouselTemplateAddCardPreview from './CarouselTemplateCardPreview/CarouselTemplateAddCardPreview';
import TouchTooltip from '../utils/TouchTooltip';
import { getDynamicColorsBySource } from '../../../../pages/ruleBasedInbox/utils';
import { renderUserAvatarDp } from '../../../../pages/ruleBasedInbox/renderUtils';


const CarouselTemplatePreview = (props: any) => {

  const { carouselFiles, selectedTemplate, compact = false, mobile = false, editable = false, handleChangeText, handleChangeCardMediaHeader, 
    getMenuDisableFlag, calculateMenuPosition, uploadFileHandlerCarousel, removeFileHandlerCarousel, isFileUploaded, handleAddNewCard, 
    handleDeleteCard, getInput, flagShowVariableTooltipForVisitWebsite, checkForAddDynamicVar, carouselInvalidId, setCarouselInvalidId, 
    inValidIdRefCarousel, handleChangeCallField, handleChangeFieldVal, handleSampleTextChangeCarousel, getWebsiteUrlValue } = props;

  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const createTemplatePayload = useTemplateLibraryStore((state) => state.createTemplatePayload);

  const { isMobileScreen } = useCarouselFunctions();

  const { integrationListNewStructure } = useContext(IntegrationContext) as IntegrationContextPayload;
  const [wabaName, setWabaName] = useState<string>('');
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const displayTemplate = editable ? createTemplatePayload : selectedTemplate; 

  const shouldShowAddCardButton = editable && !isTemplateCreated(createTemplatePayload) && (getCarouselCards(createTemplatePayload)?.length < MAX_CAROUSEL_CARDS);

  useEffect(() => {
    if (id) {
      setWabaName(getWabaName(integrationListNewStructure, id) ?? "WhatsApp Business");
    }
    else {
      setWabaName("WhatsApp Business");
    }
  }, [integrationListNewStructure, id]);

  if (!displayTemplate) {
    return <Typography>Select a carousel template to preview</Typography>;
  }


  return (
    <Card 
        className={`
            ${styles.whatsappPreview} 
            ${editable ? styles['whatsappPreview--editable'] : ''} 
            ${compact ? styles['whatsappPreview--compact'] : ''} 
            ${mobile ? styles['whatsappPreview--mobile'] : ''}
        `} 
        sx={{ position: 'relative', overflow: mobile ? 'hidden' : 'visible' }}
    >
        <Box
            ref={previewContainerRef}
            className={styles.previewContainer}
        >
            <Box
                className={styles.whatsappPreview__header}
                sx={{
                    backgroundColor: getDynamicColorsBySource("whatsapp").header_bg_color,
                    color: getDynamicColorsBySource("whatsapp").header_font_color,
                }}
            >
                <Box className={styles.whatsappPreview__headerProfile}>
                    <Box>
                        {renderUserAvatarDp(wabaName, true)}
                    </Box>

                    <Box className={styles.whatsappPreview__headerTitleWrap}>
                        <Typography variant="subtitle1" className={styles.whatsappPreview__headerTitle}>
                            {wabaName}
                        </Typography>
                    </Box>
                </Box>

                {editable && (
                    <Box className={styles.whatsappPreview__headerActions}>
                        <TouchTooltip
                            placement={isMobileScreen ? 'bottom-start' : 'right-end'}
                            title={getTooltipTextForMessageGuidelines(true)}
                        >
                            <InfoOutlinedIcon className={styles.whatsappPreview__guidelinesInfoIcon} />
                        </TouchTooltip>
                    </Box>
                )}
            </Box>

            <Box className={`${styles.whatsappPreview__messageContainer}`} sx={{ backgroundImage: `url(${whatsAppDefaultBgImg})` }}>
                
                <Box>
                
                    {editable && (
                        <CarouselTemplateMediaHeaderPreview
                            editable={editable}
                            handleChangeCardMediaHeader={handleChangeCardMediaHeader}
                        />
                    )}
                    
                    <Box className={`${styles.whatsappPreview__messageBubble} ${editable ? globalStyles.marginTop10px : ''}`}>
                        <CarouselTemplateBodyTextPreview
                            editable={editable}
                            handleChangeText={handleChangeText}
                            displayTemplate={displayTemplate}
                        />
                    </Box>

                    <Box
                        className={`${carouselStyles.carouselMessageRow} ${editable ? globalStyles.marginTop5px : ''}`}
                        sx={{ flexShrink: 0 }}
                    >
                        <CarouselTemplateCardPreview
                            carouselFiles={carouselFiles}
                            editable={editable}
                            displayTemplate={displayTemplate}
                            handleChangeText={handleChangeText}
                            uploadFileHandlerCarousel={uploadFileHandlerCarousel}
                            removeFileHandlerCarousel={removeFileHandlerCarousel}
                            isFileUploaded={isFileUploaded}
                            handleAddNewCard={handleAddNewCard}
                            handleDeleteCard={handleDeleteCard}
                            getInput={getInput}
                            flagShowVariableTooltipForVisitWebsite={flagShowVariableTooltipForVisitWebsite}
                            checkForAddDynamicVar={checkForAddDynamicVar}
                            carouselInvalidId={carouselInvalidId}
                            setCarouselInvalidId={setCarouselInvalidId}
                            inValidIdRefCarousel={inValidIdRefCarousel}
                            handleChangeCallField={handleChangeCallField}
                            handleChangeFieldVal={handleChangeFieldVal}
                            handleSampleTextChangeCarousel={handleSampleTextChangeCarousel}
                            getWebsiteUrlValue={getWebsiteUrlValue}
                        />
                    </Box>

                    {shouldShowAddCardButton && (
                        <CarouselTemplateAddCardPreview 
                            handleAddNewCard={handleAddNewCard}
                        />
                    )}

                    {editable && (
                        <CarouselTemplateInteractiveActionsPreview
                            editable={editable}
                            getMenuDisableFlag={getMenuDisableFlag}
                            calculateMenuPosition={calculateMenuPosition}
                        />
                    )}

                </Box>

            </Box>
        </Box>
    </Card>
  );
};

export default CarouselTemplatePreview;